import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { FirestoreService, ClickUpTask } from '../services/firestore.service';
import { DataService } from '../services/data.service';
import {
  Observable,
  of,
  from,
  firstValueFrom,
  first,
  combineLatest,
} from 'rxjs';
import { switchMap, map, catchError, tap } from 'rxjs/operators';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Subscription } from 'rxjs';

// AngularFire Firestore imports for modular SDK and alternate DB
import {
  Firestore,
  collectionData,
  collection as afsCollection,
  doc,
  docData,
} from '@angular/fire/firestore';
import { FirebaseApp } from '@angular/fire/app';
import { inject } from '@angular/core';
import { getFirestore } from 'firebase/firestore';

// DEVELOPMENT: Change this constant to emulate different PMs in the portal
export const DEV_EMULATE_PM = 'Hunter Hubby';

import { cbAdminFix } from '../shared/cbAdminFix';

type DropdownType =
  | 'status'
  | 'billing'
  | 'jobType'
  | 'engagementLead'
  | 'pm'
  | 'parentJobCode'
  | 'buyer'
  | 'clientPo'
  | 'clientLead'
  | 'projectType';

import jsPDF from 'jspdf';
// @ts-ignore
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

import { Title } from '@angular/platform-browser';

import { FilterBarComponent } from '../shared/components/filter-bar/filter-bar.component';

@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, FilterBarComponent],
})
export class ProjectsComponent implements OnInit, OnDestroy {
  // --- Resizable table height for projects table ---
  public projectsTableHeight = 400;
  protected resizingTable: 'projects' | 'pm' | 'el' | 'buyer' | null = null;
  protected startY = 0;
  protected startHeight = 0;

  public cbAdminFix = cbAdminFix;
  projects$: Observable<ClickUpTask[]> = of([]);
  selectedPMs: string[] = [];
  openedTaskIds: string[] = [];
  clickup_all_transformed$: Observable<string | Date | null>;
  clickup_recent_collected$: Observable<string | Date | null>;
  clickup_all_transformed_last_success$: Observable<string | Date | null>;

  sortField: string = 'jobCode';
  sortDirection: 'asc' | 'desc' = 'asc';

  // --- FILTER STATE ---
  selectedStatuses: string[] = [];
  selectedBillingStatuses: string[] = [];
  selectedJobTypes: string[] = [];
  selectedEngagementLeads: string[] = [];
  selectedBuyers: string[] = [];
  selectedClientPOs: string[] = [];
  selectedClientLeads: string[] = [];
  selectedProjectTypes: string[] = [];
  searchTerm: string = ''; // New property for global search
  allStatuses: string[] = [];
  allBillingStatuses: string[] = [];
  allJobTypes: string[] = [];
  allEngagementLeads: string[] = [];
  allBuyers: string[] = [];
  allClientPOs: string[] = [];
  filteredClientPOs: string[] = [];
  allProjectTypes: string[] = [];
  allProjects: ClickUpTask[] = [];

  // --- Parent Job Code Filter ---
  parentJobCodes: string[] = [];
  selectedParentJobCodes: string[] = [];

  // --- Job Code Filter ---
  allJobCodes: string[] = [];
  selectedJobCodes: string[] = [];

  private filterStorageKey = 'cb-ops-projects-filters';

  dropdownOpen: Record<DropdownType, boolean> = {
    status: false,
    billing: false,
    jobType: false,
    engagementLead: false,
    pm: false,
    parentJobCode: false,
    buyer: false,
    clientPo: false,
    clientLead: false,
    projectType: false,
  };

  // Use AngularFire's dependency injection for Firestore
  firestore = inject(Firestore);
  firebaseApp = inject(FirebaseApp);

  // Use AngularFire to connect to alternate Firestore DB ('clickup-data')
  programManagers$: Observable<any[]> = collectionData(
    afsCollection(
      getFirestore(this.firebaseApp, 'clickup-data'),
      'programManagers',
    ),
    { idField: 'id' },
  );

  constructor(
    protected auth: AuthService,
    protected firestoreService: FirestoreService,
    protected http: HttpClient,
    protected titleService: Title,
    protected dataService: DataService,
  ) {
    // Initialize sync-events subscription
    const syncEventsRef = doc(
      getFirestore(this.firebaseApp),
      'portal-metadata',
      'sync-events',
    );
    this.clickup_all_transformed$ = docData(syncEventsRef).pipe(
      map((event) => {
        console.log('Firestore syncEvents (all_transformed):', event);
        const val = event?.['clickup_all_transformed'];
        if (typeof val === 'string' || val instanceof Date) return val;
        return null;
      }),
    );
    // Observable for last successful all_transformed timestamp
    this.clickup_all_transformed_last_success$ = docData(syncEventsRef).pipe(
      map((event) => {
        const val = event?.['clickup_all_transformed_last_success'];
        return typeof val === 'string' || val instanceof Date ? val : null;
      }),
    );

    const syncRecentRef = doc(
      getFirestore(this.firebaseApp),
      'portal-metadata',
      'sync-events',
    );
    this.clickup_recent_collected$ = docData(syncRecentRef).pipe(
      map((event) => {
        console.log('Firestore syncEvents (recent_collected):', event);
        const val = event?.['clickup_recent_collected'];
        if (typeof val === 'string' || val instanceof Date) return val;
        return null;
      }),
    );
  }

  ngOnInit(): void {
    // Restore table height from localStorage
    const storedProj = localStorage.getItem('projects.projectsTableHeight');
    if (storedProj) this.projectsTableHeight = +storedProj;

    this.titleService.setTitle('Projects');
    // Always default to ALL for program manager
    this.selectedPMs = [];

    // Load filter state from localStorage if available
    const stored = this.getStoredFilters();
    if (stored) {
      this.selectedStatuses = stored.selectedStatuses || [];
      this.selectedBillingStatuses = stored.selectedBillingStatuses || [];
      this.selectedJobTypes = stored.selectedJobTypes || [];
      this.selectedEngagementLeads = stored.selectedEngagementLeads || [];
      this.selectedBuyers = stored.selectedBuyers || [];
      this.selectedClientPOs = stored.selectedClientPOs || [];
      this.selectedPMs = stored.selectedPMs || [];
      this.selectedClientLeads = stored.selectedClientLeads || [];
      this.selectedProjectTypes = stored.selectedProjectTypes || [];
      this.selectedParentJobCodes = stored.selectedParentJobCodes || [];
      this.selectedJobCodes = stored.selectedJobCodes || [];
      this.searchTerm = stored.searchTerm || ''; // Load search term from storage
      this.openedTaskIds = Array.isArray(stored.openedTaskIds)
        ? stored.openedTaskIds
        : [];
    }

    combineLatest([this.programManagers$, this.auth.user$]).subscribe(
      ([pms, user]) => {
        this.selectedPMs = this.selectedPMs || [];
        this.updateProjects();
      },
    );

    this.programManagers$.subscribe();

    // On first load, if no openedTaskIds in storage, close all accordions
    this.projects$.subscribe((projects) => {
      if (projects && Array.isArray(projects)) {
        const stored = this.getStoredFilters();
        if (!stored || !('openedTaskIds' in stored)) {
          this.closeAllAccordions();
        } else {
          // Restore openedTaskIds if present
          this.openedTaskIds = Array.isArray(stored.openedTaskIds)
            ? stored.openedTaskIds
            : [];
        }
      }
    });
  }

  ngOnDestroy(): void {
    // Remove any listeners if resizing was in progress
    document.removeEventListener('mousemove', this.onResizeMove as any);
    document.removeEventListener('mouseup', this.onResizeEnd as any);
  }

  /**
   * Start resizing the projects table. Mirrors BurnComponent logic.
   */
  startResize(event: MouseEvent, table: 'projects' | 'pm' | 'el' | 'buyer') {
    event.preventDefault();
    this.resizingTable = table;
    this.startY = event.clientY;
    this.startHeight = this.projectsTableHeight;
    document.addEventListener('mousemove', this.onResizeMove as any);
    document.addEventListener('mouseup', this.onResizeEnd as any);
  }

  /**
   * Mouse move handler for resizing the projects table.
   */
  onResizeMove = (event: MouseEvent) => {
    if (this.resizingTable !== 'projects') return;
    const delta = event.clientY - this.startY;
    let newHeight = this.startHeight + delta;
    if (newHeight < 120) newHeight = 120;
    if (newHeight > 1200) newHeight = 1200;
    this.projectsTableHeight = newHeight;
    localStorage.setItem('projects.projectsTableHeight', String(newHeight));
    // If a subclass wants to handle other tables, it can override this method.
  };

  /**
   * Mouse up handler to stop resizing the projects table.
   */
  onResizeEnd = () => {
    this.resizingTable = null;
    document.removeEventListener('mousemove', this.onResizeMove as any);
    document.removeEventListener('mouseup', this.onResizeEnd as any);
  };

  private _billingManuallyChanged = false;

  private saveFiltersToStorage() {
    const filters = {
      selectedStatuses: this.selectedStatuses,
      selectedBillingStatuses: this.selectedBillingStatuses,
      selectedJobTypes: this.selectedJobTypes,
      selectedEngagementLeads: this.selectedEngagementLeads,
      selectedBuyers: this.selectedBuyers,
      selectedClientPOs: this.selectedClientPOs,
      selectedPMs: this.selectedPMs,
      selectedClientLeads: this.selectedClientLeads,
      selectedParentJobCodes: this.selectedParentJobCodes,
      selectedJobCodes: this.selectedJobCodes,
      selectedProjectTypes: this.selectedProjectTypes,
      searchTerm: this.searchTerm, // Save search term to storage
      openedTaskIds: this.openedTaskIds, // Persist accordion state
    };
    localStorage.setItem(this.filterStorageKey, JSON.stringify(filters));
  }

  private getStoredFilters(): any {
    const stored = localStorage.getItem(this.filterStorageKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return {};
      }
    }
    return {};
  }

  onPMChange(event: any) {
    const val = event.target ? event.target.value : event;
    this.selectedPMs = val;
    this.saveFiltersToStorage();
    this.updateProjects();
  }

  onFilterChange() {
    if (!Array.isArray(this.selectedStatuses))
      this.selectedStatuses = this.selectedStatuses
        ? [this.selectedStatuses]
        : [];
    if (!Array.isArray(this.selectedBillingStatuses))
      this.selectedBillingStatuses = this.selectedBillingStatuses
        ? [this.selectedBillingStatuses]
        : [];
    if (!Array.isArray(this.selectedJobTypes))
      this.selectedJobTypes = this.selectedJobTypes
        ? [this.selectedJobTypes]
        : [];
    if (!Array.isArray(this.selectedEngagementLeads))
      this.selectedEngagementLeads = this.selectedEngagementLeads
        ? [this.selectedEngagementLeads]
        : [];
    if (!Array.isArray(this.selectedBuyers))
      this.selectedBuyers = this.selectedBuyers ? [this.selectedBuyers] : [];
    if (!Array.isArray(this.selectedClientPOs))
      this.selectedClientPOs = this.selectedClientPOs
        ? [this.selectedClientPOs]
        : [];
    if (!Array.isArray(this.selectedPMs))
      this.selectedPMs = this.selectedPMs ? [this.selectedPMs] : [];
    this.saveFiltersToStorage();
    this.updateProjects();
  }

  onCheckboxChange(type: DropdownType, value: string, checked: boolean) {
    switch (type) {
      case 'status':
        this.selectedStatuses = this.updateSelected(
          this.selectedStatuses,
          value,
          checked,
        );
        break;
      case 'billing':
        this.selectedBillingStatuses = this.updateSelected(
          this.selectedBillingStatuses,
          value,
          checked,
        );
        break;
      case 'jobType':
        this.selectedJobTypes = this.updateSelected(
          this.selectedJobTypes,
          value,
          checked,
        );
        break;
      case 'engagementLead':
        this.selectedEngagementLeads = this.updateSelected(
          this.selectedEngagementLeads,
          value,
          checked,
        );
        break;
      case 'buyer':
        this.selectedBuyers = this.updateSelected(
          this.selectedBuyers,
          value,
          checked,
        );
        break;
      case 'clientPo':
        this.selectedClientPOs = this.updateSelected(
          this.selectedClientPOs,
          value,
          checked,
        );
        break;
      case 'clientLead':
        this.selectedClientLeads = this.updateSelected(
          this.selectedClientLeads,
          value,
          checked,
        );
        break;
      case 'pm':
        this.selectedPMs = this.updateSelected(
          this.selectedPMs,
          value,
          checked,
        );
        break;
      case 'parentJobCode':
        this.selectedParentJobCodes = this.updateSelected(
          this.selectedParentJobCodes,
          value,
          checked,
        );
        break;
      case 'projectType':
        this.selectedProjectTypes = this.updateSelected(
          this.selectedProjectTypes,
          value,
          checked,
        );
        break;
    }
    // Track if billing was manually changed
    if (type === 'billing') this._billingManuallyChanged = true;
    this.saveFiltersToStorage();
    this.updateProjects();
  }

  updateSelected(arr: string[], value: string, checked: boolean): string[] {
    if (checked) {
      return arr.includes(value) ? arr : [...arr, value];
    } else {
      return arr.filter((v) => v !== value);
    }
  }

  onDropdownChange(type: DropdownType, event: Event) {
    // ngModel already updates the selected array, so just trigger filter update
    this.onFilterChange();
  }

  onParentJobCodeChange(code: string, checked: boolean) {
    if (checked) {
      if (!this.selectedParentJobCodes.includes(code)) {
        this.selectedParentJobCodes.push(code);
      }
    } else {
      const idx = this.selectedParentJobCodes.indexOf(code);
      if (idx !== -1) this.selectedParentJobCodes.splice(idx, 1);
    }
    this.saveFiltersToStorage();
    this.onFilterChange();
  }

  onJobCodeChange(code: string, checked: boolean) {
    if (checked) {
      if (!this.selectedJobCodes.includes(code)) {
        this.selectedJobCodes.push(code);
      }
    } else {
      const idx = this.selectedJobCodes.indexOf(code);
      if (idx !== -1) this.selectedJobCodes.splice(idx, 1);
    }
    this.saveFiltersToStorage();
    this.onFilterChange();
  }

  // Handle search term changes
  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.saveFiltersToStorage();
    this.updateProjects();
  }

  updateProjects() {
    let obs: Observable<ClickUpTask[]>;
    // Load portal-data with year-based collections (when feature flag enabled)
    obs = this.dataService.getCollection<ClickUpTask>('portal-data').pipe(
      map((projects) =>
        projects.filter((project) => {
          if (!this.selectedPMs || this.selectedPMs.length === 0) return true;
          const pm = project['programManager'];
          if (!pm) return false;
          if (typeof pm === 'string') {
            return this.selectedPMs.includes(pm);
          }
          if (typeof pm === 'object' && pm.username) {
            return this.selectedPMs.includes(pm.username);
          }
          return false;
        }),
      ),
    );
    // --- FILTER AND SORT PIPELINE ---
    this.projects$ = obs.pipe(
      tap((projects: any[]) => {
        this.allProjects = projects;
        this.updateParentJobCodes();
        const statuses = new Set<string>();
        const billingStatuses = new Set<string>();
        const jobTypes = new Set<string>();
        const engagementLeads = new Set<string>();
        const buyers = new Set<string>();
        const clientPOs = new Set<string>();
        const projectTypes = new Set<string>();
        const jobCodes = new Set<string>();
        projects.forEach((task) => {
          if (task.status) statuses.add(task.status);
          if (task.billingStatus) billingStatuses.add(task.billingStatus);
          if (task.jobType) jobTypes.add(task.jobType);
          if (task.engagementLead) engagementLeads.add(task.engagementLead);
          if (task.buyer) buyers.add(task.buyer);
          if (task.clientPo) clientPOs.add(task.clientPo);
          if (task.projectType) projectTypes.add(task.projectType);
          if (task.jobCode) jobCodes.add(task.jobCode);
        });
        this.allStatuses = Array.from(statuses).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allBillingStatuses = Array.from(billingStatuses).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allJobTypes = Array.from(jobTypes).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allEngagementLeads = Array.from(engagementLeads).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allBuyers = Array.from(buyers).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allClientPOs = Array.from(clientPOs).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allProjectTypes = Array.from(projectTypes).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allJobCodes = Array.from(jobCodes).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
      }),
      map((projects: any[]) => {
        // --- Filtering logic (status, billing, job type, engagement lead) ---
        let filteredProjects = projects;
        if (this.selectedStatuses.length > 0) {
          filteredProjects = filteredProjects.filter((project) =>
            this.selectedStatuses.includes(project.status),
          );
        }
        if (this.selectedBillingStatuses.length > 0) {
          filteredProjects = filteredProjects.filter((project) =>
            this.selectedBillingStatuses.includes(project['billingStatus']),
          );
        }
        if (this.selectedJobTypes.length > 0) {
          filteredProjects = filteredProjects.filter((project) =>
            this.selectedJobTypes.includes(project['jobType']),
          );
        }
        if (this.selectedEngagementLeads.length > 0) {
          filteredProjects = filteredProjects.filter((project) =>
            this.selectedEngagementLeads.includes(project['engagementLead']),
          );
        }
        if (this.selectedBuyers.length > 0) {
          filteredProjects = filteredProjects.filter((project) =>
            this.selectedBuyers.includes(project['buyer']),
          );
        }
        if (this.selectedClientPOs.length > 0) {
          filteredProjects = filteredProjects.filter((project) =>
            this.selectedClientPOs.includes(
              project['clientPo'] || project['clientPO'],
            ),
          );
        }
        if (this.selectedClientLeads.length > 0) {
          filteredProjects = filteredProjects.filter((project) =>
            this.selectedClientLeads.includes(project['clientLead']),
          );
        }
        if (
          this.selectedParentJobCodes &&
          this.selectedParentJobCodes.length > 0 &&
          !this.selectedParentJobCodes.includes('All')
        ) {
          filteredProjects = filteredProjects.filter(
            (project: any) =>
              project.jobCode &&
              this.selectedParentJobCodes.some((code) =>
                project.jobCode.startsWith(code),
              ),
          );
        }
        if (this.selectedJobCodes && this.selectedJobCodes.length > 0) {
          filteredProjects = filteredProjects.filter((project: any) =>
            this.selectedJobCodes.includes(project.jobCode),
          );
        }
        if (this.selectedProjectTypes.length > 0) {
          filteredProjects = filteredProjects.filter((project) =>
            this.selectedProjectTypes.includes(project['projectType']),
          );
        }
        // Global search across all fields
        if (this.searchTerm && this.searchTerm.trim() !== '') {
          const searchTermLower = this.searchTerm.toLowerCase().trim();
          // Check if any field contains the search term
          const searchableFields = [
            'projectName',
            'taskName',
            'jobCode',
            'status',
            'billingStatus',
            'jobType',
            'engagementLead',
            'buyer',
            'programManager',
            'clientLead',
            'toplineUpdate',
            'projectType',
            'practiceArea',
            'clientPO',
          ];
          // Check if any field contains the search term
          filteredProjects = filteredProjects.filter((project: any) => {
            return searchableFields.some((field) => {
              const value = project[field];
              return value
                ? value.toString().toLowerCase().includes(searchTermLower)
                : false;
            });
          });
        }

        // Cascading filter: Only show POs that are referenced by visible projects
        // Extract all unique clientPo values from filtered projects
        const visibleClientPOs = new Set(
          filteredProjects
            .map((p) => p.clientPo || p.clientPO)
            .filter((po) => po && po.trim() !== ''),
        );

        // Filter POs to ONLY those referenced by visible projects
        this.filteredClientPOs = this.allClientPOs.filter((po) =>
          visibleClientPOs.has(po),
        );

        return filteredProjects;
      }),
      map((projects: any[]) => {
        // --- Sorting logic (unchanged) ---
        return [...projects].sort((a, b) => {
          let aVal = a[this.sortField] ?? '';
          let bVal = b[this.sortField] ?? '';
          if (typeof aVal === 'string') aVal = aVal.toLowerCase();
          if (typeof bVal === 'string') bVal = bVal.toLowerCase();
          if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }),
      tap((projects) => {
        // Always show all possible filter options from the full project set
        this.allStatuses = Array.from(
          new Set(
            this.allProjects
              .map((t) => t.status)
              .filter((v) => v != null && v !== ''),
          ),
        ).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allBillingStatuses = Array.from(
          new Set(
            this.allProjects
              .map((t) => t['billingStatus'])
              .filter((v) => v != null && v !== ''),
          ),
        ).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allJobTypes = Array.from(
          new Set(
            this.allProjects
              .map((t) => t['jobType'])
              .filter((v) => v != null && v !== ''),
          ),
        ).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allEngagementLeads = Array.from(
          new Set(
            this.allProjects
              .map((t) => t['engagementLead'])
              .filter((v) => v != null && v !== ''),
          ),
        ).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allBuyers = Array.from(
          new Set(
            this.allProjects
              .map((t) => t['buyer'])
              .filter((v) => v != null && v !== ''),
          ),
        ).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allClientPOs = Array.from(
          new Set(
            this.allProjects
              .map((t) => t['clientPo'] || t['clientPO'])
              .filter((v) => v != null && v !== ''),
          ),
        ).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        this.allProjectTypes = Array.from(
          new Set(
            this.allProjects
              .map((t) => t['projectType'])
              .filter((v) => v != null && v !== ''),
          ),
        ).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
      }),
      catchError((err) => {
        return of([]);
      }),
    );
  }

  updateParentJobCodes() {
    const allProjects = this.allProjects || [];
    const parentJobCodeSet = new Set<string>();
    for (const project of allProjects) {
      if (project.jobCode) {
        const match = project.jobCode.match(/^[A-Z0-9]+/);
        if (match) parentJobCodeSet.add(match[0]);
      }
    }
    this.parentJobCodes = Array.from(parentJobCodeSet).sort();
  }

  get allProgramManagers(): string[] {
    return Array.from(
      new Set(
        (this.allProjects || [])
          .map((p) =>
            typeof p['programManager'] === 'string' &&
            p['programManager'].trim() !== ''
              ? p['programManager']
              : undefined,
          )
          .filter((val): val is string => typeof val === 'string'),
      ),
    ).sort();
  }

  get allClientLeads(): string[] {
    return Array.from(
      new Set(
        (this.allProjects || [])
          .map((p) =>
            typeof p['clientLead'] === 'string' && p['clientLead'].trim() !== ''
              ? p['clientLead']
              : undefined,
          )
          .filter((val): val is string => typeof val === 'string'),
      ),
    ).sort();
  }

  setSort(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.updateProjects();
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  trackByProjectId(index: number, project: any): string {
    return project['taskId'] || project.id || index;
  }

  toggleAccordion(projectId: string) {
    if (this.openedTaskIds.includes(projectId)) {
      this.openedTaskIds = this.openedTaskIds.filter((id) => id !== projectId);
    } else {
      this.openedTaskIds = [...this.openedTaskIds, projectId];
    }
    this.saveFiltersToStorage();
  }

  openAllAccordions(projects: any[]) {
    this.openedTaskIds = projects.map((project) => project.taskId);
    this.saveFiltersToStorage();
  }

  closeAllAccordions() {
    this.openedTaskIds = [];
    this.saveFiltersToStorage();
  }

  toggleDropdown(type: DropdownType) {
    const newDropdownOpen = { ...this.dropdownOpen };
    for (const key in newDropdownOpen) {
      if (key !== type) {
        newDropdownOpen[key as keyof typeof newDropdownOpen] = false;
      }
    }
    newDropdownOpen[type] = !this.dropdownOpen[type];
    this.dropdownOpen = newDropdownOpen;
  }

  closeAllDropdowns() {
    this.dropdownOpen = Object.keys(this.dropdownOpen).reduce(
      (acc, key) => ({ ...acc, [key]: false }),
      {} as Record<DropdownType, boolean>,
    );
  }

  // Utility to check if a value is a valid date
  isValidDate(val: any): boolean {
    if (!val) return false;
    if (val instanceof Date && !isNaN(val.getTime())) return true;
    if (typeof val === 'number') return true;
    if (typeof val === 'string' && !isNaN(Date.parse(val))) return true;
    return false;
  }

  // Utility to check if a value is a valid number (for currency fields)
  isValidNumber(val: any): boolean {
    return (
      val !== null && val !== '' && !Array.isArray(val) && !isNaN(Number(val))
    );
  }

  // Utility: Replace 'Cannonball General Admin' with 'CB Admin' for display
  // DEPRECATED: Use cbAdminFix instead
  replaceGeneralAdmin(val: string): string {
    return cbAdminFix(val);
  }

  resetFilters() {
    this.selectedStatuses = [];
    this.selectedBillingStatuses = [];
    this.selectedJobTypes = [];
    this.selectedEngagementLeads = [];
    this.selectedBuyers = [];
    this.selectedClientPOs = [];
    this.selectedPMs = [];
    this.selectedClientLeads = [];
    this.selectedProjectTypes = [];
    this.selectedParentJobCodes = [];
    this.selectedJobCodes = [];
    this.searchTerm = ''; // Reset search term
    localStorage.removeItem(this.filterStorageKey);
    this.updateProjects();
  }

  // --- EXPORT BUTTON HANDLERS ---
  exportProjectsCSV(): void {
    // Export all displayed fields, including those in the accordion body
    const aoa: any[][] = [
      [
        'FY',
        'Job Code',
        'Project Name',
        'Task Name',
        'Client Lead',
        'Client PO',
        'Status',
        'Progress',
        'Price',
        'Spent',
        'Remaining',
        'Harvest Budget',
        'Harvest Remaining',
        'Updated',
        'Billing Status',
        'Job Type',
        'Engagement Lead',
        'Engagement Lead Initials',
        'Budget',
        'Start Date',
        'Due Date',
        'Created Date',
        'Brief URL',
        'Dropbox URL',
        'GDrive URL',
        'Figma URL',
        'Frame URL',
        'Sharepoint URL',
        'Task ID',
        'Space ID',
        'Space Name',
        'Folder ID',
        'Folder Name',
        'List ID',
        'List Name',
        'Topline Update',
        'Buyer',
        'Project Type',
        'Practice Area',
        'Program Manager',
        'The Foundry Project',
        'Archived',
        'Time Estimate',
      ],
    ];
    this.projects$.subscribe((projects) => {
      aoa.push(
        ...projects.map((project: any) => [
          project.fiscalYear,
          project.jobCode,
          project.projectName,
          project.taskName,
          project.clientLead,
          project['clientPo'] || project['clientPO'],
          project.clickupStatus || project.status,
          this.formatPercent
            ? this.formatPercent(project.progress)
            : project.progress + '%',
          this.formatCurrency
            ? this.formatCurrency(project.price)
            : project.price,
          this.formatCurrency
            ? this.formatCurrency(project.expenses)
            : project.expenses,
          this.formatCurrency
            ? this.formatCurrency(
                (project.price || 0) -
                  ((project.price || 0) * (project.progress ?? 0)) / 100,
              )
            : (project.price || 0) -
              ((project.price || 0) * (project.progress ?? 0)) / 100,
          this.formatCurrency
            ? this.formatCurrency(project.harvestBudget)
            : project.harvestBudget,
          this.formatCurrency
            ? this.formatCurrency(project.harvestRemaining)
            : project.harvestRemaining,
          project.updatedDate,
          project.billingStatus,
          project.jobType,
          this.cbAdminFix
            ? this.cbAdminFix(project.engagementLead)
            : project.engagementLead,
          project.engagementLeadInitials,
          project.budget,
          project.startDate,
          project.dueDate,
          project.createdDate,
          project.briefUrl,
          project.dropboxUrl,
          project.gdriveUrl,
          project.figmaUrl,
          project.frameUrl,
          project.sharepointUrl,
          project.taskId,
          project.spaceId,
          project.spaceName,
          project.folderId,
          project.folderName,
          project.listId,
          project.listName,
          project.toplineUpdate,
          project['buyer'],
          project.projectType,
          project.practiceArea,
          this.replaceGeneralAdmin
            ? this.replaceGeneralAdmin(project.programManager)
            : project.programManager,
          project.theFoundryProject ? 'Yes' : 'No',
          project.archived ? 'Yes' : 'No',
          project.timeEstimate,
        ]),
      );
      this.exportAoAToCSV(
        aoa,
        `CB-Projects-${this.getExportSuffix ? this.getExportSuffix() : ''}.csv`,
      );
    });
  }

  exportProjectsXLSX(): void {
    // Export all displayed fields, including those in the accordion body
    this.projects$.subscribe((projects) => {
      const aoa: any[][] = [
        [
          'FY',
          'Job Code',
          'Project Name',
          'Task Name',
          'Client Lead',
          'Client PO',
          'Status',
          'Progress',
          'Price',
          'Spent',
          'Remaining',
          'Harvest Budget',
          'Harvest Remaining',
          'Updated',
          'Billing Status',
          'Job Type',
          'Engagement Lead',
          'Engagement Lead Initials',
          'Budget',
          'Start Date',
          'Due Date',
          'Created Date',
          'Brief URL',
          'Dropbox URL',
          'GDrive URL',
          'Figma URL',
          'Frame URL',
          'Sharepoint URL',
          'Task ID',
          'Space ID',
          'Space Name',
          'Folder ID',
          'Folder Name',
          'List ID',
          'List Name',
          'Topline Update',
          'Buyer',
          'Project Type',
          'Practice Area',
          'Program Manager',
          'The Foundry Project',
          'Archived',
          'Time Estimate',
        ],
        ...projects.map((project: any) => [
          project.fiscalYear,
          project.jobCode,
          project.projectName,
          project.taskName,
          project.clientLead,
          project['clientPo'] || project['clientPO'],
          project.clickupStatus || project.status,
          this.formatPercent
            ? this.formatPercent(project.progress)
            : project.progress + '%',
          this.formatCurrency
            ? this.formatCurrency(project.price)
            : project.price,
          this.formatCurrency
            ? this.formatCurrency(project.expenses)
            : project.expenses,
          this.formatCurrency
            ? this.formatCurrency(
                (project.price || 0) -
                  ((project.price || 0) * (project.progress ?? 0)) / 100,
              )
            : (project.price || 0) -
              ((project.price || 0) * (project.progress ?? 0)) / 100,
          this.formatCurrency
            ? this.formatCurrency(project.harvestBudget)
            : project.harvestBudget,
          this.formatCurrency
            ? this.formatCurrency(project.harvestRemaining)
            : project.harvestRemaining,
          project.updatedDate,
          project.billingStatus,
          project.jobType,
          this.cbAdminFix
            ? this.cbAdminFix(project.engagementLead)
            : project.engagementLead,
          project.engagementLeadInitials,
          project.budget,
          project.startDate,
          project.dueDate,
          project.createdDate,
          project.briefUrl,
          project.dropboxUrl,
          project.gdriveUrl,
          project.figmaUrl,
          project.frameUrl,
          project.sharepointUrl,
          project.taskId,
          project.spaceId,
          project.spaceName,
          project.folderId,
          project.folderName,
          project.listId,
          project.listName,
          project.toplineUpdate,
          project['buyer'],
          project.projectType,
          project.practiceArea,
          this.replaceGeneralAdmin
            ? this.replaceGeneralAdmin(project.programManager)
            : project.programManager,
          project.theFoundryProject ? 'Yes' : 'No',
          project.archived ? 'Yes' : 'No',
          project.timeEstimate,
        ]),
      ];
      const worksheet = XLSX.utils.aoa_to_sheet(aoa);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Projects');
      XLSX.writeFile(
        workbook,
        `CB-Projects-${this.getExportSuffix ? this.getExportSuffix() : ''}.xlsx`,
      );
    });
  }

  exportProjectsPDF(): void {
    // Generate PDF using jsPDF and autoTable (matches burn.component)
    this.projects$.subscribe((projects) => {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });
      const columns = [
        { header: 'FY', dataKey: 'fiscalYear' },
        { header: 'Job Code', dataKey: 'jobCode' },
        { header: 'Project Name', dataKey: 'projectName' },
        { header: 'Task Name', dataKey: 'taskName' },
        { header: 'Client Lead', dataKey: 'clientLead' },
        { header: 'Client PO', dataKey: 'clientPo' },
        { header: 'Status', dataKey: 'clickupStatus' },
        { header: 'Progress', dataKey: 'progress' },
        { header: 'Price', dataKey: 'price' },
        { header: 'Spent', dataKey: 'expenses' },
        { header: 'Remaining', dataKey: 'remaining' },
        { header: 'Harvest Budget', dataKey: 'harvestBudget' },
        { header: 'Harvest Remaining', dataKey: 'harvestRemaining' },
        { header: 'Updated', dataKey: 'updatedDate' },
        { header: 'Billing Status', dataKey: 'billingStatus' },
        { header: 'Job Type', dataKey: 'jobType' },
        { header: 'Engagement Lead', dataKey: 'engagementLead' },
        {
          header: 'Engagement Lead Initials',
          dataKey: 'engagementLeadInitials',
        },
        { header: 'Budget', dataKey: 'budget' },
        { header: 'Start Date', dataKey: 'startDate' },
        { header: 'Due Date', dataKey: 'dueDate' },
        { header: 'Created Date', dataKey: 'createdDate' },
        { header: 'Brief URL', dataKey: 'briefUrl' },
        { header: 'Dropbox URL', dataKey: 'dropboxUrl' },
        { header: 'GDrive URL', dataKey: 'gdriveUrl' },
        { header: 'Figma URL', dataKey: 'figmaUrl' },
        { header: 'Frame URL', dataKey: 'frameUrl' },
        { header: 'Sharepoint URL', dataKey: 'sharepointUrl' },
        { header: 'Task ID', dataKey: 'taskId' },
        { header: 'Space ID', dataKey: 'spaceId' },
        { header: 'Space Name', dataKey: 'spaceName' },
        { header: 'Folder ID', dataKey: 'folderId' },
        { header: 'Folder Name', dataKey: 'folderName' },
        { header: 'List ID', dataKey: 'listId' },
        { header: 'List Name', dataKey: 'listName' },
        { header: 'Topline Update', dataKey: 'toplineUpdate' },
        { header: 'Buyer', dataKey: 'buyer' },
        { header: 'Project Type', dataKey: 'projectType' },
        { header: 'Practice Area', dataKey: 'practiceArea' },
        { header: 'Program Manager', dataKey: 'programManager' },
        { header: 'The Foundry Project', dataKey: 'theFoundryProject' },
        { header: 'Archived', dataKey: 'archived' },
        { header: 'Time Estimate', dataKey: 'timeEstimate' },
      ];
      const rows = projects.map((project: any) => ({
        fiscalYear: project.fiscalYear ?? '',
        jobCode: project.jobCode ?? '',
        projectName: project.projectName ?? '',
        taskName: project.taskName ?? '',
        clientLead: project.clientLead ?? '',
        clientPo: project['clientPo'] ?? '',
        clickupStatus: project.clickupStatus ?? '',
        progress: this.formatPercent
          ? this.formatPercent(project.progress)
          : project.progress + '%',
        price: this.formatCurrency
          ? this.formatCurrency(project.price)
          : project.price,
        expenses: this.formatCurrency
          ? this.formatCurrency(project.expenses)
          : project.expenses,
        remaining: this.formatCurrency
          ? this.formatCurrency(
              (project.price || 0) -
                ((project.price || 0) * (project.progress ?? 0)) / 100,
            )
          : (project.price || 0) -
            ((project.price || 0) * (project.progress ?? 0)) / 100,
        harvestBudget: this.formatCurrency
          ? this.formatCurrency(project.harvestBudget)
          : (project.harvestBudget ?? ''),
        harvestRemaining: this.formatCurrency
          ? this.formatCurrency(project.harvestRemaining)
          : (project.harvestRemaining ?? ''),
        updatedDate: project.updatedDate ?? '',
        billingStatus: project.billingStatus ?? '',
        jobType: project.jobType ?? '',
        engagementLead: this.cbAdminFix
          ? this.cbAdminFix(project.engagementLead)
          : (project.engagementLead ?? ''),
        engagementLeadInitials: project.engagementLeadInitials ?? '',
        budget: project.budget ?? '',
        startDate: project.startDate ?? '',
        dueDate: project.dueDate ?? '',
        createdDate: project.createdDate ?? '',
        briefUrl: project.briefUrl ?? '',
        dropboxUrl: project.dropboxUrl ?? '',
        gdriveUrl: project.gdriveUrl ?? '',
        figmaUrl: project.figmaUrl ?? '',
        frameUrl: project.frameUrl ?? '',
        sharepointUrl: project.sharepointUrl ?? '',
        taskId: project.taskId ?? '',
        spaceId: project.spaceId ?? '',
        spaceName: project.spaceName ?? '',
        folderId: project.folderId ?? '',
        folderName: project.folderName ?? '',
        listId: project.listId ?? '',
        listName: project.listName ?? '',
        toplineUpdate: project.toplineUpdate ?? '',
        buyer: project['buyer'] ?? '',
        projectType: project.projectType ?? '',
        practiceArea: project.practiceArea ?? '',
        programManager: this.replaceGeneralAdmin
          ? this.replaceGeneralAdmin(project.programManager)
          : (project.programManager ?? ''),
        theFoundryProject: project.theFoundryProject ? 'Yes' : 'No',
        archived: project.archived ? 'Yes' : 'No',
        timeEstimate: project.timeEstimate ?? '',
      }));
      autoTable(doc, {
        columns,
        body: rows,
        margin: { top: 40, left: 40, right: 40, bottom: 40 },
        headStyles: { fillColor: [194, 24, 7], textColor: 255 },
        styles: { fontSize: 10 },
        didDrawPage: (data: any) => {
          doc.setFontSize(16);
          doc.text('Projects', data.settings.margin.left, 30);
        },
      });
      doc.save(
        `CB-Projects-${this.getExportSuffix ? this.getExportSuffix() : ''}.pdf`,
      );
    });
  }

  // --- Utility for CSV export ---
  exportAoAToCSV(aoa: any[][], filename: string) {
    // Standard CSV export logic
    const csv = aoa
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // --- Utility for filename suffix ---
  getExportSuffix(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const yy = pad(now.getFullYear() % 100);
    const MM = pad(now.getMonth() + 1);
    const dd = pad(now.getDate());
    const hh = pad(now.getHours());
    const mm = pad(now.getMinutes());
    return `${yy}${MM}${dd}T${hh}${mm}`;
  }

  formatPercent(val: any): string {
    return typeof val === 'number' ? `${val}%` : (val ?? '');
  }

  formatCurrency(val: any): string {
    if (typeof val === 'number')
      return val.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      });
    return val ?? '';
  }

  public userTimeZone: string = (() => {
    try {
      const date = new Date();
      // This gives something like "PDT" or "PST" for US time zones
      return (
        date
          .toLocaleTimeString('en-us', { timeZoneName: 'short' })
          .split(' ')
          .pop() || ''
      );
    } catch {
      return '';
    }
  })();
}
